import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useLocation } from 'react-router-dom'
import { Menu, Transition } from '@headlessui/react'
import { 
  UserCircleIcon, 
  ArrowRightOnRectangleIcon,
  ClockIcon,
  ChartBarIcon,
  UserIcon
} from '@heroicons/react/24/outline'
import { RootState, AppDispatch } from '../store'
import { logout } from '../store/slices/authSlice'
import { resetTimer } from '../store/slices/timerSlice'
import CytestLink from './CytestLink'
import clsx from 'clsx'
import { profileAPI } from '../services/profileAPI'

const Navbar: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>()
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useSelector((state: RootState) => state.auth)
  const { isRunning, remainingTimeSeconds } = useSelector((state: RootState) => state.timer)
  const hasResetRef = useRef(false)
  const isOnActualTestPage = useMemo(() => {
    const normalizedPath = location.pathname.toLowerCase()
    if (!normalizedPath.startsWith('/test/')) {
      return false
    }

    const excludedSegments = [
      '/instructions',
      '/system-check',
      '/completed',
      '/history',
    ]

    return !excludedSegments.some(segment => normalizedPath.includes(segment))
  }, [location.pathname])
  const [profilePictureUrl, setProfilePictureUrl] = useState<string | null>(null)
  
  // Get profile picture from API
  const getProfilePicture = async () => {
    if (!user?.id) return null
    
    try {
      const profile = await profileAPI.getProfile()
      return profile.profile_picture_url || null
    } catch (error) {
      console.error('Failed to load profile picture:', error)
      return null
    }
  }
  
  // Update profile picture when component mounts, when navigating to profile page, or when user changes
  useEffect(() => {
    const loadProfilePicture = async () => {
      const pictureUrl = await getProfilePicture()
      setProfilePictureUrl(pictureUrl)
    }
    loadProfilePicture()
  }, [location.pathname, user])
  
  // Listen for profile updates
  useEffect(() => {
    const handleProfileUpdate = async () => {
      const pictureUrl = await getProfilePicture()
      setProfilePictureUrl(pictureUrl)
    }
    
    window.addEventListener('profileUpdated', handleProfileUpdate)
    
    return () => {
      window.removeEventListener('profileUpdated', handleProfileUpdate)
    }
  }, [])
  
  // Reset timer when not on actual test page (exclude instructions and system-check pages)
  useEffect(() => {
    if (isOnActualTestPage) {
      hasResetRef.current = false
      return
    }

    if (hasResetRef.current) {
      return
    }

    if (isRunning || remainingTimeSeconds > 0) {
      console.log('Not on actual test page, resetting timer')
      dispatch(resetTimer())
      hasResetRef.current = true
    }
  }, [isOnActualTestPage, isRunning, remainingTimeSeconds, dispatch])

  const handleLogout = () => {
    dispatch(resetTimer())
    dispatch(logout())
    navigate('/login')
  }

  const formatTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const remainingSecs = seconds % 60

    if (hours > 0) {
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${remainingSecs.toString().padStart(2, '0')}`
    } else {
      return `${minutes.toString().padStart(2, '0')}:${remainingSecs.toString().padStart(2, '0')}`
    }
  }

  return (
    <nav className="navbar sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Logo and title */}
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CytestLink className="text-xl font-bold text-primary-600" />
            </div>
            
          </div>

          {/* User menu */}
          <div className="flex items-center">
            <Menu as="div" className="relative ml-3">
              <div>
                <Menu.Button className="flex items-center space-x-3 text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 p-2">
                  {profilePictureUrl ? (
                    <img
                      src={profilePictureUrl}
                      alt="Profile"
                      className="h-8 w-8 rounded-full object-cover border-2 border-gray-200"
                    />
                  ) : (
                    <UserCircleIcon className="h-8 w-8 text-gray-400" />
                  )}
                  <div className="hidden md:block text-left">
                    <div className="text-sm font-medium text-gray-900">
                      {user?.first_name === 'Master' && user?.last_name === 'Administrator' 
                        ? 'Administrator' 
                        : `${user?.first_name} ${user?.last_name}`}
                    </div>
                    <div className="text-xs text-gray-500 capitalize">
                      {user?.role}
                    </div>
                  </div>
                </Menu.Button>
              </div>
              
              <Transition
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Menu.Items className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none">
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={() => navigate('/dashboard')}
                        className={clsx(
                          active ? 'bg-gray-100' : '',
                          'block w-full text-left px-4 py-2 text-sm text-gray-700 flex items-center space-x-2'
                        )}
                      >
                        <ChartBarIcon className="h-4 w-4" />
                        <span>Dashboard</span>
                      </button>
                    )}
                  </Menu.Item>
                  
                  {/* Profile menu - only show for candidates */}
                  {user?.role === 'candidate' && (
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={() => navigate('/profile')}
                          className={clsx(
                            active ? 'bg-gray-100' : '',
                            'block w-full text-left px-4 py-2 text-sm text-gray-700 flex items-center space-x-2'
                          )}
                        >
                          <UserIcon className="h-4 w-4" />
                          <span>Profile</span>
                        </button>
                      )}
                    </Menu.Item>
                  )}
                  
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={handleLogout}
                        className={clsx(
                          active ? 'bg-gray-100' : '',
                          'block w-full text-left px-4 py-2 text-sm text-gray-700 flex items-center space-x-2'
                        )}
                      >
                        <ArrowRightOnRectangleIcon className="h-4 w-4" />
                        <span>Sign out</span>
                      </button>
                    )}
                  </Menu.Item>
                </Menu.Items>
              </Transition>
            </Menu>
          </div>
        </div>
      </div>
    </nav>
  )
}

export default Navbar
